import { useState } from 'react';
import { useExemploCriterio } from '@/api/criterios-rateio';
import { ErroLista } from '@/components/cargos';
import { useFormatoNoBlur } from '@/hooks/use-formato-no-blur';
import { useDebounced } from '@/hooks/use-debounced';
import { decimalDigitado, formatarDecimal, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body tabular focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const MAX_CASAS = 6;

/**
 * Painel Exemplo: o valor total digitado vai para a API (GET /criterios-rateio/:id/exemplo) com debounce de 400 ms.
 * Nenhum % ou R$ e calculado aqui: a tabela mostra `linhas`, `somaPercentual` e `somaValor` como vieram.
 */
export function ExemploCriterio({ criterioId }: { criterioId: string }) {
  const [texto, setTexto] = useState('');
  const campo = useFormatoNoBlur(texto, 'dinheiro');
  const digitado = decimalDigitado(texto);
  const muitasCasas = digitado !== null && (digitado.split('.')[1]?.length ?? 0) > MAX_CASAS;
  const valor = digitado !== null && !muitasCasas ? digitado : undefined;
  const valorAtrasado = useDebounced(valor, 400);
  const exemplo = useExemploCriterio(criterioId, valorAtrasado);
  const dados = exemplo.data;

  let corpo;
  if (!valor) {
    corpo = <p className="px-1 py-6 text-center text-small text-ink-muted">{muitasCasas ? `Use no máximo ${MAX_CASAS} casas decimais.` : 'Digite um valor total para ver o rateio.'}</p>;
  } else if (exemplo.isError && !dados) {
    corpo = <ErroLista erro={exemplo.error} onTentar={() => void exemplo.refetch()} titulo="Não foi possível calcular o exemplo" />;
  } else if (!dados || valorAtrasado !== valor) {
    corpo = <p role="status" className="px-1 py-6 text-center text-small text-ink-muted">Calculando…</p>;
  } else {
    corpo = (
      <>
        {dados.erro && (
          <p role="status" className="mb-3 rounded-xl border border-warning-dot bg-warning-bg px-3 py-2 text-small text-warning-ink" data-testid="exemplo-erro">
            {dados.erro.message}
          </p>
        )}
        <table className="w-full border-collapse text-small" aria-label="Exemplo de rateio">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-2 pr-3 font-medium">Centro</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Base</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">%</th>
              <th scope="col" className="py-2 pl-3 text-right font-medium">R$</th>
            </tr>
          </thead>
          <tbody>
            {dados.linhas.map((l) => (
              <tr key={l.centroId} data-testid="linha-exemplo" className="border-t border-line-soft">
                <td className="py-2 pr-3">
                  {l.caminho} — {l.nome}
                </td>
                <td className="tabular px-3 py-2 text-right">{formatarDecimal(l.base, 0)}</td>
                <td className="tabular px-3 py-2 text-right">{formatarFracaoPercentual(l.percentual)}</td>
                <td className="tabular py-2 pl-3 text-right">{formatarMoeda(l.valor)}</td>
              </tr>
            ))}
          </tbody>
          {dados.linhas.length > 0 && (
            <tfoot>
              <tr className="border-t border-border font-semibold">
                <td className="py-2 pr-3">Total</td>
                <td />
                <td className="tabular px-3 py-2 text-right" data-testid="soma-percentual">{formatarFracaoPercentual(dados.somaPercentual)}</td>
                <td className="tabular py-2 pl-3 text-right" data-testid="soma-valor">{formatarMoeda(dados.somaValor)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {dados.linhas.length === 0 && !dados.erro && <p className="px-1 py-4 text-center text-small text-ink-muted">Nenhum centro considerado.</p>}
      </>
    );
  }

  return (
    <section aria-label="Exemplo do critério" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      <h3 className="text-body font-semibold">Exemplo</h3>
      <div>
        <label htmlFor="ex-valor" className="mb-1.5 block text-caption font-medium text-ink-secondary">
          Valor total (R$)
        </label>
        <input
          id="ex-valor"
          inputMode="decimal"
          autoComplete="off"
          className={CAMPO}
          value={campo.value}
          onFocus={campo.onFocus}
          onBlur={campo.onBlur}
          onChange={(e) => setTexto(e.target.value)}
        />
      </div>
      {corpo}
    </section>
  );
}
